import { apiClient } from '@/api/client';
import {
  Business,
  PathsAdminBusinessesGetParametersQueryStatus,
} from '@/api/models';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { Pagination } from '@/components/ui/Pagination';
import {
  isJobActive,
  isJobComplete,
  useBackgroundJob,
  useBackgroundJobActions,
} from '@/hooks/useBackgroundJob';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { useNotifications } from '@/lib/notifications';
import { queryKeys } from '@/lib/queryKeys';
import {
  mdiCalculator,
  mdiCancel,
  mdiPause,
  mdiRefresh,
  mdiStore,
} from '@mdi/js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { BusinessesFilters } from './BusinessesFilters';
import { BusinessesTable } from './BusinessesTable';

export interface BusinessesListProps {
  onRowClick: (business: Business) => void;
  onEditBusiness: (business: Business) => void;
  onDeleteBusiness: (business: Business) => void;
  isDeleting: boolean;
}

export const BusinessesList: React.FC<BusinessesListProps> = ({
  onRowClick,
  onEditBusiness,
  onDeleteBusiness,
  isDeleting,
}) => {
  const { addNotification } = useNotifications();
  const queryClient = useQueryClient();
  const [activeJobId, setActiveJobId] = React.useState<number | null>(null);

  const { data: jobProgress } = useBackgroundJob(activeJobId);
  const { pauseJob, cancelJob } = useBackgroundJobActions();

  const recalculateScoresMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.POST('/admin/scores/recalculate-all');
      if (response.error) {
        throw new Error('Failed to trigger score recalculation');
      }
      return response.data;
    },
    onSuccess: data => {
      setActiveJobId(data?.jobId ?? null);
      addNotification({
        type: 'success',
        title: 'Score Recalculation Started',
        message: `${data?.queued} businesses queued for score recalculation`,
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to trigger score recalculation',
      });
    },
  });

  React.useEffect(() => {
    if (jobProgress && isJobComplete(jobProgress.status)) {
      if (jobProgress.status === 'completed') {
        addNotification({
          type: 'success',
          title: 'Score Recalculation Complete',
          message: `Successfully recalculated ${jobProgress.successfulItems} scores (${jobProgress.failedItems} failed)`,
        });
        queryClient.invalidateQueries({
          queryKey: [queryKeys.ADMIN_BUSINESSES],
        });
      }
      setActiveJobId(null);
    }
  }, [jobProgress, addNotification, queryClient]);

  const handleRecalculateScores = () => {
    if (
      window.confirm(
        'Are you sure you want to recalculate scores for ALL businesses? This may take a while.',
      )
    ) {
      recalculateScoresMutation.mutate();
    }
  };

  const handlePauseJob = () => {
    if (activeJobId) {
      pauseJob.mutate(activeJobId);
    }
  };

  const handleCancelJob = () => {
    if (activeJobId && window.confirm('Are you sure you want to cancel?')) {
      cancelJob.mutate(activeJobId, {
        onSuccess: () => setActiveJobId(null),
      });
    }
  };

  const { state, list, actions, isLoading } = usePaginatedList<
    Business,
    { status: string; categoryId: string; country: string; city: string }
  >({
    queryKey: queryKeys.ADMIN_BUSINESSES,
    endpoint: '/admin/businesses',
    initialFilters: { status: '', categoryId: '', country: '', city: '' },
    buildQueryParams: ({
      page,
      limit,
      search,
      sortBy,
      sortOrder,
      filters,
    }) => ({
      page,
      limit,
      search: search || undefined,
      status:
        (filters.status as PathsAdminBusinessesGetParametersQueryStatus) ||
        undefined,
      categoryId: filters.categoryId ? parseInt(filters.categoryId) : undefined,
      country: filters.country || undefined,
      city: filters.city || undefined,
      sortBy,
      sortOrder,
    }),
  });

  const isJobRunning = jobProgress && isJobActive(jobProgress.status);

  return (
    <div className='space-y-6'>
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        <div>
          <h1 className='text-3xl font-bold text-grey-900 flex items-center'>
            <Icon path={mdiStore} size={32} className='mr-3' />
            Businesses
          </h1>
          <p className='text-grey-600 mt-2'>
            Manage businesses and their information
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-2 sm:gap-3'>
          <div className='text-sm text-grey-600'>
            <Icon path={mdiStore} size={16} className='mr-1 inline' />
            {list.total} total businesses
          </div>
          {isJobRunning ? (
            <div className='flex items-center space-x-2'>
              <div className='flex flex-col items-end mr-2'>
                <span className='text-xs text-grey-500'>Recalculating...</span>
                <div className='flex items-center'>
                  <div className='w-32 bg-grey-200 rounded-full h-2 mr-2'>
                    <div
                      className='bg-yellow-500 h-2 rounded-full transition-all'
                      style={{ width: `${jobProgress.percentComplete}%` }}
                    />
                  </div>
                  <span className='text-xs font-medium'>
                    {jobProgress.processedItems}/{jobProgress.totalItems}
                  </span>
                </div>
              </div>
              <Button
                icon={mdiPause}
                color='light'
                title='Pause'
                onClick={handlePauseJob}
                loading={pauseJob.isPending}
                small
              />
              <Button
                icon={mdiCancel}
                color='danger'
                title='Cancel'
                onClick={handleCancelJob}
                loading={cancelJob.isPending}
                small
              />
            </div>
          ) : (
            <Button
              icon={mdiCalculator}
              color='primary'
              label='Recalculate Scores'
              title='Recalculate work-friendly scores for all businesses'
              onClick={handleRecalculateScores}
              loading={recalculateScoresMutation.isPending}
            />
          )}
          <Button
            icon={mdiRefresh}
            color='light'
            title='Refresh'
            onClick={actions.refetch}
            loading={isLoading}
          />
        </div>
      </div>

      <BusinessesFilters
        searchTerm={state.search}
        selectedStatus={state.filters.status}
        selectedCategory={state.filters.categoryId}
        selectedCountry={state.filters.country}
        selectedCity={state.filters.city}
        onSearchChange={actions.setSearch}
        onStatusChange={value => actions.setFilter('status', value)}
        onCategoryChange={value => actions.setFilter('categoryId', value)}
        onCountryChange={value => actions.setFilter('country', value)}
        onCityChange={value => actions.setFilter('city', value)}
        onClearFilters={actions.clearFilters}
      />

      <BusinessesTable
        businesses={list.items}
        isLoading={isLoading}
        sortBy={state.sortBy}
        sortOrder={state.sortOrder}
        onSort={actions.setSort}
        onRowClick={onRowClick}
        onEditBusiness={onEditBusiness}
        onDeleteBusiness={onDeleteBusiness}
        isDeleting={isDeleting}
        selectedCountry={state.filters.country}
        selectedCity={state.filters.city}
      />

      {list.totalPages > 1 && (
        <Pagination
          currentPage={list.currentPage}
          totalPages={list.totalPages}
          onPageChange={actions.setPage}
          totalItems={list.total}
          itemsPerPage={list.limit}
        />
      )}
    </div>
  );
};
